import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useGenerateCommunityShareableLink } from '@/api/endpoints/community/useGenerateCommunityShareableLink';
import { useSendCommunityInviteEmails } from '@/api/endpoints/community/useSendCommunityInviteEmails';
import { Copy02Icon } from '@/assets/icons/Copy02Icon';
import { Mail01Icon } from '@/assets/icons/Mail01Icon';

import FormTextField from '@/components/form/FormTextField';

interface ShareCommunityModalProps {
  open: boolean;
  onClose: () => void;
  communityName: string;
  communityId: number;
}

interface FormData {
  emails: string;
}

const ShareCommunityModal: React.FC<ShareCommunityModalProps> = ({
  open,
  onClose,
  communityName,
  communityId,
}) => {
  const [copied, setCopied] = useState(false);
  const [shareableLink, setShareableLink] = useState('');

  const form = useForm<FormData>({
    defaultValues: {
      emails: '',
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
    watch,
  } = form;

  const generateLinkMutation = useGenerateCommunityShareableLink();
  const sendInvitesMutation = useSendCommunityInviteEmails();

  const handleClose = () => {
    reset();
    setCopied(false);
    setShareableLink(''); // Reset link so it's regenerated next time
    onClose();
  };

  // Generate shareable link when modal opens
  useEffect(() => {
    if (open && !shareableLink && !generateLinkMutation.isPending) {
      generateLinkMutation.mutate(
        {
          community_id: communityId,
        },
        {
          onSuccess: data => {
            setShareableLink(data.shareable_link);
          },
          onError: () => {
            enqueueSnackbar('Failed to generate shareable link', { variant: 'error' });
          },
        }
      );
    }
  }, [open, shareableLink, communityId, generateLinkMutation]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareableLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Watch email field for validation
  const emailsValue = watch('emails');

  useEffect(() => {
    if (!emailsValue || !emailsValue.trim()) {
      clearErrors('emails');
      return;
    }

    const emailArray = emailsValue
      .split(',')
      .map(email => email.trim())
      .filter(email => email);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const invalidEmails = emailArray.filter(email => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      setError('emails', {
        type: 'manual',
        message: `Invalid email(s): ${invalidEmails.join(', ')}`,
      });
    } else if (emailArray.length > 50) {
      setError('emails', {
        type: 'manual',
        message: 'Maximum 50 email addresses allowed',
      });
    } else {
      clearErrors('emails');
    }
  }, [emailsValue, setError, clearErrors]);

  const onSubmit = (data: FormData) => {
    if (!shareableLink) {
      return;
    }

    const emailArray = data.emails
      .split(',')
      .map(email => email.trim())
      .filter(email => email);

    sendInvitesMutation.mutate(
      {
        community_id: communityId,
        shareable_link: shareableLink,
        emails: emailArray,
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Invitations sent successfully!', { variant: 'success' });
          handleClose();
        },
        onError: () => {
          enqueueSnackbar('Failed to send invitations', { variant: 'error' });
        },
      }
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite Members to "{communityName}"</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Shareable Link Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Share via link
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Anyone with this link can request to join the community
            </Typography>
            {generateLinkMutation.isPending ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={56}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <TextField
                fullWidth
                value={shareableLink}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                        <IconButton onClick={handleCopyLink} edge="end">
                          <Copy02Icon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: 'grey.50',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey.300',
                    },
                  },
                }}
              />
            )}
          </Box>

          {/* Email Invites Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Send email invites
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter email addresses separated by commas (max 50)
            </Typography>
            <FormTextField
              name="emails"
              control={control}
              placeholder="john@example.com, jane@example.com"
              multiline
              rows={3}
              error={!!errors.emails}
              helperText={errors.emails?.message}
              textFieldProps={{
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: 'flex-start' }}>
                      <Mail01Icon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={
            !!errors.emails ||
            !emailsValue?.trim() ||
            sendInvitesMutation.isPending ||
            !shareableLink
          }
          startIcon={
            sendInvitesMutation.isPending ? <CircularProgress size={20} /> : <Mail01Icon />
          }
        >
          {sendInvitesMutation.isPending ? 'Sending...' : 'Send Invites'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareCommunityModal;
